import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import {
    type BaseActivatedPanelProps,
    mapLimitedListOfItemsToTopNWithCost,
    type SelectionDropdown,
    type SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { useColumnPickerControls } from "app/pages/sales-intelligence/hooks/useColumnPickerControls";
import { QuotaExportCostTag } from "app/pages/sales-intelligence/modules/quota/components/QuotaExportCostTag";
import { QuotaTooltipContent } from "app/pages/sales-intelligence/modules/quota/components/QuotaTooltipContent/QuotaTooltipContent";
import SelectionDDQuotaItem from "app/pages/sales-intelligence/modules/quota/components/SelectionDDQuotaItem/SelectionDDQuotaItem";
import { SelectionDropdownQuotaFooter } from "app/pages/sales-intelligence/modules/quota/components/SelectionDropdownQuotaFooter/SelectionDropdownQuotaFooter";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import { useLeadsExportCost } from "app/pages/sales-intelligence/modules/quota/hooks/useLeadsExportCost";
import { useLowQuotaBannerManagement } from "app/pages/sales-intelligence/modules/quota/hooks/useLowQuotaBannerManagement";
import getLimitedListOfItems from "app/pages/sales-intelligence/modules/quota/utils/getLimitedListOfItems";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { StyledExportQuotaTagContainer } from "app/pages/sales-intelligence/sub-modules/company-search/components/WebsitesExcelExportPanel/styles";
import { MAX_LEADS_CRM_EXPORT_SELECTION_TOP_N_VALUE } from "app/pages/sales-intelligence/sub-modules/company-search/constants/websites-table";
import type { Translatable } from "app/pages/sales-intelligence/types";

import type TopNWithCostDropdownItem from "../../../../common-components/table-actions-panel/models/selection-dropdown-item/TopNWithCostDropdownItem/TopNWithCostDropdownItem";
import BaseActivatedPanel from "../../../table-actions-panel/components/BaseActivatedPanel/BaseActivatedPanel";
import {
    StyledInnerContainer,
    StyledPanelIcon,
} from "../../../table-actions-panel/components/BaseActivatedPanel/styles";
import SelectionDDItem from "../../../table-actions-panel/components/SelectionDDItem/SelectionDDItem";
import useActionsPanelState from "../../../table-actions-panel/hooks/useActionsPanelState";
import ManualSelectionItem from "../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";

import { StyledInfoMessage, StyledSelectionDropdown, StyledSubmitButton, StyledTitle } from "./styles";

type WebsitesCRMExportPanelViewProps = BaseActivatedPanelProps & {
    label: Readonly<Translatable>;
    isDisabled: boolean;
    isLoading: boolean;
    numberOfSelectedItems: number;
    numberOfRemainingExports: number;
    numberOfResults: number;
    selectionDropdown: SelectionDropdown;
    onSelectionChange(item: Readonly<SelectionDropdownItem>): void;
    onSubmit(activeSelectionItem: Readonly<SelectionDropdownItem>): void;
    exportType?: ExportCostTypeItem;
};

const WebsitesCRMExportPanelView = (props: WebsitesCRMExportPanelViewProps) => {
    const {
        isDisabled,
        isLoading,
        label,
        numberOfSelectedItems,
        numberOfRemainingExports,
        numberOfResults,
        selectionDropdown,
        exportType = ExportCostTypeItem.Excel,
        onSelectionChange,
        onSubmit,
    } = props;

    const { openWithDataCost } = useColumnPickerControls();

    const {
        quota,
        pricePerRecord,
        isLowQuota: checkIsLowQuota,
        getExportCost,
        canAfford,
    } = useLeadsExportCost({ exportType });

    const handleMappingLimitedListOfItems = (listOfItems: readonly number[]): readonly TopNWithCostDropdownItem[] => {
        const possibleTopNValues = getLimitedListOfItems(
            listOfItems,
            quota.data.remaining,
            pricePerRecord,
            numberOfResults,
            MAX_LEADS_CRM_EXPORT_SELECTION_TOP_N_VALUE,
        );

        return mapLimitedListOfItemsToTopNWithCost(possibleTopNValues);
    };

    const state = useActionsPanelState(selectionDropdown, {
        numberOfResults,
        numberOfSelectedItems,
        onSelectionChange,
        limit: numberOfRemainingExports,
        mapLimitedListOfItems: salesSettingsHelper.isDataCreditsEnabled ? handleMappingLimitedListOfItems : undefined,
    });

    const isLowQuota = checkIsLowQuota(state.activeSelectionItem.value);
    const isSubmitDisabled = isDisabled || state.isSelectionEmpty || isLowQuota;

    const { lowCreditsBannerConfigs } = useLowQuotaBannerManagement({
        requiredCredits: getExportCost(state.activeSelectionItem.value),
    });

    useDidUpdate(() => {
        if (!isLoading) {
            onSelectionChange(state.activeSelectionItem);
        }
    }, [isLoading]);

    function handleSubmit() {
        onSubmit(state.activeSelectionItem);
    }

    return (
        <BaseActivatedPanel onDeactivate={props.onDeactivate}>
            <StyledInnerContainer>
                <StyledPanelIcon
                    iconName="upload"
                    size="xs"
                />
                <I18n
                    data-automation="actions-panel.export-label"
                    element={StyledTitle}
                    translatable={label}
                />
                <StyledSelectionDropdown
                    buttonText={state.activeSelectionItem.getSelectedTranslation()}
                    isDisabled={isDisabled}
                    isLoading={isLoading}
                    isHideFooterDivider
                    footer={
                        <SelectionDropdownQuotaFooter
                            totalQuota={quota.data.total}
                            remainingQuota={quota.data.remaining}
                            pricePerRecord={pricePerRecord}
                            minOptionValue={state.dropdownItems[0]?.value}
                            maxOptionValue={state.dropdownItems[state.dropdownItems.length - 1]?.value}
                        />
                    }
                    onSelect={state.setActiveSelectionItem}
                >
                    {state.dropdownItems
                        .map((item) => (
                            <SelectionDDQuotaItem
                                key={item.id}
                                item={item}
                                isSelected={state.activeSelectionItem.id === item.id}
                                getOperationCost={getExportCost}
                                canAffordOperation={canAfford}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                item={state.manualSelectionItem}
                                key={ManualSelectionItem.ID}
                                isSelected={state.activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </StyledSelectionDropdown>
                {state.activeSelectionItem ? (
                    <StyledExportQuotaTagContainer>
                        <QuotaExportCostTag
                            quota={getExportCost(state.activeSelectionItem.value)}
                            pricePerRecord={pricePerRecord}
                            isLowQuota={isLowQuota}
                            withTooltip={!isLowQuota}
                            onExportCostChange={() => {
                                openWithDataCost(quota.data.remaining, getExportCost(state.activeSelectionItem.value));
                                props.onDeactivate();
                            }}
                        />
                    </StyledExportQuotaTagContainer>
                ) : null}
                <StyledSubmitButton
                    isDisabled={isSubmitDisabled}
                    isLoading={isLoading}
                    text={translate("si.common.button.export")}
                    tooltipContent={
                        isSubmitDisabled && isLowQuota ? (
                            <QuotaTooltipContent
                                title={translate(lowCreditsBannerConfigs.titleKey)}
                                description={translate(lowCreditsBannerConfigs.descriptionKey)}
                                buttonText={
                                    lowCreditsBannerConfigs.buttonTextKey
                                        ? translate(lowCreditsBannerConfigs.buttonTextKey)
                                        : undefined
                                }
                                onClick={lowCreditsBannerConfigs.onClick}
                            />
                        ) : (
                            translate("si.multi_selector.tooltip.excel.unselected_domains")
                        )
                    }
                    onClick={handleSubmit}
                />
                <I18n
                    data-automation="info-message-text"
                    element={StyledInfoMessage}
                    translatable={{ key: "si.multi_selector.crm_export_info" }}
                />
            </StyledInnerContainer>
        </BaseActivatedPanel>
    );
};

export default WebsitesCRMExportPanelView;
